// Breeze on a phone: a strip that drops from the top of the call instead of a
// sheet from the bottom. Adam, 2026-10-02: "come down from a bar at the top
// taking up like 1/4 of the screen and have the most relevant thing, like a
// question(s), note(s), and you can click into it to expand, or swipe it to be
// even smaller".
//
// Three states, remembered for the call:
//   card      about a quarter of the screen, one card at a time; swipe sideways
//             for the next, tap to open the full panel, swipe up to fold it
//   peek      one line and a handle; swipe down or tap to bring the card back
//   expanded  the full tabbed panel as a sheet from the top; swipe up on its
//             handle to fold it back to the card
//
// The card list is ranked in packages/core (stripCards), which also leaves out
// every private card while the person shares their screen.
//
// In a call on a phone it is public only: the shared notes, questions and next
// steps, with no goal, chat, suggestions or host switches (Adam, 2026-10-04:
// "Let's actually disable breeze, except for public stuff like notes, on
// mobile entirely. That will be expandable or retractable from the top of the
// screen"). /listen, the operator's own phone screen, keeps all of it.

import { useEffect, useMemo, useRef, useState, type JSX } from "react";
import { stripCards, type StripCard } from "@breeze/core";
import type { AssistantStore } from "../hooks/useAssistant";
import type { PanelStore } from "../hooks/usePanel";
import { useSwipe, type SwipeDirection } from "../hooks/useSwipe";
import { BreezePanel, type RecordingShown } from "./BreezePanel";
import { BreezeMarkIcon, CloseIcon } from "./icons";

type Mode = "card" | "peek" | "expanded";

interface BreezeStripProps {
  readonly panel: PanelStore;
  readonly assistant: AssistantStore;
  readonly selfId: string | null;
  readonly isHost: boolean;
  readonly sharingScreen: boolean;
  /** Shared items only: no private cards, no Mine or Host tab. */
  readonly publicOnly?: boolean;
  /** Whether the call is being recorded (see BreezePanel). */
  readonly recording?: RecordingShown;
  readonly onClose: () => void;
}

const MODE_KEY = (): string => `breeze.strip.v1.${window.location.pathname}`;

function readMode(): Mode {
  try {
    const v = window.sessionStorage.getItem(MODE_KEY());
    return v === "peek" || v === "expanded" ? v : "card";
  } catch {
    return "card";
  }
}

function writeMode(mode: Mode): void {
  try {
    window.sessionStorage.setItem(MODE_KEY(), mode);
  } catch {
    // Storage can be blocked; the strip still works for this visit.
  }
}

export const LABELS: Record<StripCard["kind"], string> = {
  say: "Say next",
  suggested: "Suggested question",
  question: "Question",
  note: "Note",
  step: "Next step",
};

export function metaOf(card: StripCard): string {
  const parts: string[] = [];
  if (card.private) parts.push("Private");
  if (card.byBreeze) parts.push("Added by Breeze");
  else if (card.by) parts.push(card.by);
  if (card.kind === "step" && card.owner) parts.push(`For ${card.owner}`);
  return parts.join(" · ");
}

export function BreezeStrip({ panel, assistant, selfId, isHost, sharingScreen, publicOnly = false, recording, onClose }: BreezeStripProps): JSX.Element {
  const [mode, setModeState] = useState<Mode>(readMode);
  const setMode = (next: Mode): void => {
    setModeState(next);
    writeMode(next);
  };

  const say = !publicOnly && isHost && assistant.state?.on === true ? (assistant.host?.say ?? null) : null;
  const cards = useMemo(
    () =>
      stripCards({
        isHost,
        // stripCards leaves out every private card while sharing; public only
        // asks for exactly that.
        sharingScreen: sharingScreen || publicOnly,
        shared: panel.shared,
        priv: panel.priv,
        suggestions: panel.suggestions,
        say,
      }),
    [isHost, sharingScreen, publicOnly, panel.shared, panel.priv, panel.suggestions, say],
  );

  // Which card is showing is kept by id, so a list that grows underneath does
  // not move the card out from under a thumb.
  const [selected, setSelected] = useState<string | null>(null);
  const found = cards.findIndex((c) => c.id === selected);
  const index = found >= 0 ? found : 0;
  const current = cards[index] ?? null;
  const [direction, setDirection] = useState<"next" | "prev" | null>(null);

  const go = (to: number): void => {
    if (cards.length < 2) return;
    const next = (to + cards.length) % cards.length;
    setDirection(to > index ? "next" : "prev");
    setSelected(cards[next]?.id ?? null);
  };

  // A card that was not there a moment ago pulses the peek once and nothing
  // else: no focus moves, the open card is not swapped for the new one.
  const seen = useRef<Set<string>>(new Set(cards.map((c) => c.id)));
  const [fresh, setFresh] = useState<{ readonly id: string; readonly n: number } | null>(null);
  useEffect(() => {
    const added = cards.filter((c) => !seen.current.has(c.id));
    for (const c of cards) seen.current.add(c.id);
    const last = added[added.length - 1];
    if (last) setFresh((f) => ({ id: last.id, n: (f?.n ?? 0) + 1 }));
  }, [cards]);

  const peekCard = (fresh && cards.find((c) => c.id === fresh.id)) || cards[0] || null;

  useEffect(() => {
    if (mode === "expanded") return;
    const onKey = (e: KeyboardEvent): void => {
      // Escape closes the call's open menu first, and only then the strip.
      if (e.key === "Escape" && !document.querySelector('[role="menu"]')) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [mode, onClose]);

  if (mode === "expanded") {
    return (
      <>
        <div className="bp-scrim" aria-hidden="true" onPointerDown={() => setMode("card")} />
        <BreezePanel
          panel={panel}
          assistant={assistant}
          selfId={selfId}
          isHost={isHost}
          sharingScreen={sharingScreen}
          publicOnly={publicOnly}
          recording={recording}
          layout="top"
          onClose={onClose}
          onCollapse={() => setMode("card")}
        />
      </>
    );
  }

  if (mode === "peek") {
    return (
      <Peek
        // Remounting on a new item restarts the one pulse.
        key={fresh?.n ?? 0}
        card={peekCard}
        pulse={fresh?.n ?? 0}
        onOpen={() => setMode("card")}
        onClose={onClose}
      />
    );
  }

  return (
    <StripCardView
      card={current}
      position={cards.length > 0 ? { at: index + 1, of: cards.length } : null}
      direction={direction}
      loading={panel.shared === null}
      onOpen={() => setMode("expanded")}
      onFold={() => setMode("peek")}
      onStep={(d) => go(index + (d === "left" ? 1 : -1))}
      onClose={onClose}
    />
  );
}

interface StripCardViewProps {
  readonly card: StripCard | null;
  readonly position: { readonly at: number; readonly of: number } | null;
  readonly direction: "next" | "prev" | null;
  readonly loading: boolean;
  readonly onOpen: () => void;
  readonly onFold: () => void;
  readonly onStep: (direction: "left" | "right") => void;
  readonly onClose: () => void;
}

function StripCardView({ card, position, direction, loading, onOpen, onFold, onStep, onClose }: StripCardViewProps): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  useSwipe(ref, {
    follow: ["left", "right", "up"],
    onTap: onOpen,
    onSwipe: (d: SwipeDirection) => {
      if (d === "up") onFold();
      else if (d === "left" || d === "right") onStep(d);
    },
  });

  const text = card?.text ?? (loading ? "" : "Nothing yet. Tap to add a note or a question.");
  const label = card ? LABELS[card.kind] : "Breeze";
  const meta = card ? metaOf(card) : "";

  return (
    <div
      ref={ref}
      className="bpstrip"
      role="region"
      aria-label="Breeze"
      aria-roledescription="carousel"
      data-testid="breeze-strip"
      data-mode="card"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "ArrowRight") onStep("left");
        else if (e.key === "ArrowLeft") onStep("right");
        else if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
    >
      <div className="bpstrip__top">
        <span className="bpstrip__mark" aria-hidden="true">
          <BreezeMarkIcon size={18} />
        </span>
        <span className="bpstrip__label" data-testid="strip-label">
          {label}
        </span>
        {position && position.of > 1 ? (
          <span className="bpstrip__count" data-testid="strip-count">
            {position.at} of {position.of}
          </span>
        ) : null}
        <button type="button" className="bpstrip__close" aria-label="Close Breeze" data-nodrag="" onClick={onClose}>
          <CloseIcon size={20} />
        </button>
      </div>
      {loading && !card ? (
        <div className="bpstrip__loading" role="status" aria-label="Loading">
          <span />
          <span />
        </div>
      ) : (
        <p
          key={card?.id ?? "empty"}
          className={`bpstrip__text${direction ? ` bpstrip__text--${direction}` : ""}${card ? "" : " bpstrip__text--quiet"}`}
          data-long={text.length > 90 ? "1" : "0"}
          data-testid="strip-text"
        >
          {text}
        </p>
      )}
      {meta ? <p className="bpstrip__meta">{meta}</p> : <span className="bpstrip__meta" />}
      <div className="bpstrip__handle" aria-hidden="true">
        <span />
      </div>
    </div>
  );
}

interface PeekProps {
  readonly card: StripCard | null;
  readonly pulse: number;
  readonly onOpen: () => void;
  readonly onClose: () => void;
}

function Peek({ card, pulse, onOpen, onClose }: PeekProps): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  useSwipe(ref, {
    follow: ["down"],
    onTap: onOpen,
    onSwipe: (d) => {
      if (d === "down") onOpen();
    },
  });
  return (
    <div
      ref={ref}
      className={`bpstrip bpstrip--peek${pulse > 0 ? " bpstrip--pulse" : ""}`}
      role="region"
      aria-label="Breeze"
      data-testid="breeze-strip"
      data-mode="peek"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
    >
      <div className="bpstrip__row">
        <span className="bpstrip__mark" aria-hidden="true">
          <BreezeMarkIcon size={18} />
        </span>
        <span className="bpstrip__line" data-testid="peek-text">
          {card ? card.text : "Breeze"}
        </span>
        <button type="button" className="bpstrip__close" aria-label="Close Breeze" data-nodrag="" onClick={onClose}>
          <CloseIcon size={20} />
        </button>
      </div>
      <div className="bpstrip__handle bpstrip__handle--peek" aria-hidden="true">
        <span />
      </div>
    </div>
  );
}
